import React from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * A component that conditionally renders children based on user roles or subscription
 * 
 * @param {Object} props
 * @param {string} [props.requiredRole] - The role required to view this component ('org-admin', 'global-admin', 'member')
 * @param {string|string[]} [props.requiredSubscription] - The subscription(s) required ('starter', 'professional', 'trial')
 * @param {React.ReactNode} [props.fallback=null] - Content to show if user doesn't have required permissions
 * @param {React.ReactNode} props.children - The content to render if user has access
 */
const ProtectedComponent = ({ 
  requiredRole, 
  requiredSubscription,
  fallback = null,
  children 
}) => {
  const { isOrgAdmin, isGlobalAdmin, subscription, trialInfo } = useAuth();

  // Role check
  let hasRole = true;
  if (requiredRole) {
    hasRole =
      (requiredRole === 'org-admin' && isOrgAdmin) ||
      (requiredRole === 'global-admin' && isGlobalAdmin) ||
      (requiredRole === 'member'); // All authenticated users are members
  }

  // Subscription check
  let hasSubscription = true;
  if (requiredSubscription) {
    const required = Array.isArray(requiredSubscription)
      ? requiredSubscription
      : [requiredSubscription];
    
    // Check if user has the required subscription level
    const hasRequiredLevel = required.some(level => {
      if (level === 'professional') {
        return (
          (subscription?.productName === 'Professional' && subscription?.status === 'active') ||
          (subscription?.status === 'trialing') ||
          trialInfo?.isInTrial
        );
      } else if (level === 'starter') {
        return (
          (subscription?.productName === 'Starter' && subscription?.status === 'active') ||
          (subscription?.productName === 'Professional' && subscription?.status === 'active') ||
          (subscription?.status === 'trialing') ||
          trialInfo?.isInTrial
        );
      }
      return false;
    });
    
    hasSubscription = hasRequiredLevel;
  }

  if (!hasRole || !hasSubscription) return fallback;

  return children;
};

export default ProtectedComponent; 